import type { ReactNode } from "react";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const img = (name: string) => `${import.meta.env.BASE_URL}images/${name}`;

/** Full-bleed photographic hero with a readable left-weighted overlay. */
export function PhotoHero({ src, alt, position = "center", overlay, eyebrow, children, testId }: {
  src: string; alt: string; position?: string; overlay: string; eyebrow: string; children: ReactNode; testId: string;
}) {
  return (
    <section className="relative overflow-hidden text-white min-h-[420px] lg:min-h-[480px] flex items-center py-12 lg:py-16" data-testid={testId}>
      <img src={img(src)} alt={alt} width={1600} height={900} fetchPriority="high" decoding="async"
        className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: position }} />
      <div className={cn("absolute inset-0", overlay)} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="max-w-3xl [text-shadow:0_1px_12px_rgba(0,0,0,0.35)]">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/85 mb-4">
            <span className="h-px w-8 bg-white/70" />{eyebrow}
          </p>
          {children}
        </div>
      </div>
    </section>
  );
}

export type Step = { title: string; desc: string };

/** Photo + numbered process list, compact two-column. */
export function PhotoProcess({ title, intro, steps, photo, alt, caption, accent, reverse, position = "center", testId }: {
  title: string; intro: string; steps: Step[]; photo: string; alt: string; caption: string; accent: string; reverse?: boolean; position?: string; testId: string;
}) {
  return (
    <section className="py-12 lg:py-16 bg-background" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-6xl grid gap-8 lg:grid-cols-2 items-center">
        <figure className={cn("relative", reverse && "lg:order-2")}>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-border shadow-sm bg-muted">
            <img src={img(photo)} alt={alt} width={800} height={600} loading="lazy" decoding="async"
              className="w-full h-full object-cover" style={{ objectPosition: position }} />
          </div>
          <figcaption className="mt-2 text-xs text-muted-foreground">{caption}</figcaption>
        </figure>
        <div>
          <h2 className="text-2xl md:text-3xl font-bold font-display mb-3">{title}</h2>
          <p className="text-muted-foreground mb-5">{intro}</p>
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={s.title} className="flex gap-3">
                <span className={cn("w-7 h-7 shrink-0 rounded-full text-xs font-bold flex items-center justify-center text-white", accent)}>{i + 1}</span>
                <div>
                  <h3 className="font-semibold font-display leading-tight">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

export type GalleryItem = { src: string; alt: string; caption: string; position?: string };

/** Small captioned photo gallery of real site work. */
export function WorkGallery({ title, intro, items, testId }: { title: string; intro: string; items: GalleryItem[]; testId: string }) {
  return (
    <section className="py-12 lg:py-16 bg-slate-50 border-t border-border" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="mb-6 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-bold font-display mb-2">{title}</h2>
          <p className="text-muted-foreground">{intro}</p>
        </div>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it, i) => (
            <figure key={it.src} className="bg-card rounded-xl overflow-hidden border border-border shadow-sm" data-testid={`${testId}-item-${i}`}>
              <div className="aspect-[4/3] overflow-hidden bg-muted">
                <img src={img(it.src)} alt={it.alt} width={600} height={450} loading="lazy" decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" style={{ objectPosition: it.position ?? "center" }} />
              </div>
              <figcaption className="p-3 text-sm text-muted-foreground leading-snug">{it.caption}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Compact checklist card grid. */
export function Checklist({ title, intro, items, iconClass, testId }: { title: string; intro: string; items: Step[]; iconClass: string; testId: string }) {
  return (
    <section className="py-12 lg:py-14 bg-background border-t border-border" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="mb-6 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-bold font-display mb-2">{title}</h2>
          <p className="text-muted-foreground">{intro}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((s) => (
            <div key={s.title} className="flex gap-3 p-4 rounded-xl bg-slate-50 border border-border">
              <CheckCircle2 className={cn("w-5 h-5 shrink-0 mt-0.5", iconClass)} />
              <div>
                <h3 className="font-semibold font-display leading-tight mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export type Faq = { q: string; a: string };

/** Accessible FAQ accordion, compact two-column on large screens. */
export function FaqSection({ title, intro, faqs, testId }: { title: string; intro: string; faqs: Faq[]; testId: string }) {
  return (
    <section className="py-12 lg:py-16 bg-background border-t border-border" data-testid={testId}>
      <div className="container mx-auto px-4 max-w-6xl grid gap-8 lg:grid-cols-[1fr_2fr]">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold font-display mb-2">{title}</h2>
          <p className="text-muted-foreground">{intro}</p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`${testId}-item-${i}`}>
              <AccordionTrigger className="text-left font-semibold font-display">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
